//src/App.js
import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { jwtDecode } from "jwt-decode";
import { clarity } from 'react-microsoft-clarity';

import './index.css';
import SOURCE_MAP from "./config/sourceMap";
import { ToastContainer } from 'react-toastify';
import Layout from "./Layout";
import LoginPage from "./pages/LoginPage";
import JumboMailLogin from "./pages/JumboMailLogin";
import SignupPage from "./pages/SignupPage";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import CreateBulkOrder from "./pages/CreateBulkOrder";
import BulkTracking from "./pages/BulkTracking";
import CreateForwardOrder from "./pages/CreateForwardOrder";
import Wallet from "./pages/Wallet";
import Tools from "./pages/Tools";
import AllShipments from "./pages/AllShipments";
import ShipmentDetails from "./pages/ShipmentDetails";
import CreateReverseOrder from "./pages/CreateReverseOrder";
import ClientOnboarding from "./pages/ClientOnboarding";
import GenerateLabel from "./pages/GenerateLabel";
import Profile from "./pages/Profile";
import SearchUnprocessedOrdersPage from "./pages/SearchUnprocessedOrdersPage";
import Reports from "./pages/Reports";
import ShippingBulkLabelsDownload from "./pages/ShippingBulkLabelsDownload";
import RequireAuth from "./components/RequireAuth";
import { restoreSession, logout, markInitialized } from './redux/slices/authSlice';
import SelfShipOrders from "./pages/marketplace/SelfShipOrders";
import PublicTrackingPage from './pages/public-tracking/PublicTrackingPage';
import RootListener from './RootListener';
import ChannelOrdersPage from './pages/channel-order/ChannelOrdersPage';
import { ToastProvider } from "./components/toast/ToastProvider";
import CodRemittance from "./pages/remittance/CodRemittance";
import PickupManifest from "./pages/manifest/PickupManifest";
import NDRManagement from "./pages/ndr-management/NDRManagement";
import ClaimTracker from "./pages/claim/ClaimTracker";
import MerchantOnboarding from "./pages/MerchantOnboarding";

const App = () => {
  const dispatch = useDispatch();

  const reduxToken = useSelector((state) => state.auth.token);
  const token = reduxToken || localStorage.getItem("authToken") || "";

  const isLoggedIn = useSelector((state) => state.auth.isAuthenticated);

  const clarityId = process.env.REACT_APP_CLARITY_ID;

  const getLoginRouteForCurrentSource = () => {
    try {
      const s = localStorage.getItem("source") || "wayuvega";
      return (SOURCE_MAP[s] && SOURCE_MAP[s].loginRoute) || "/login";
    } catch {
      return "/login";
    }
  };

  useEffect(() => {
    if (!clarityId) return;
    clarity.init(clarityId);

    if (token) {
      try {
        const { username } = jwtDecode(token);
        if (clarity.hasStarted) {
          clarity.identify("USER_ID", { username });
        }
      } catch (err) {
        console.warn("Invalid token for Clarity identify:", err);
      }
    }
  }, [clarityId, token]);

  // On cold start, rehydrate Redux from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem("authToken");
    if (!storedToken) {   
      dispatch(markInitialized());
      return;
    }

    try {
      const decoded = jwtDecode(storedToken);
      const { exp, merchantId, parentClientId } = decoded;
      
      if (exp > Date.now() / 1000) {
        // Pass user object as expected by restoreSession reducer
        dispatch(restoreSession({ 
          token: storedToken, 
          user: { merchantId, parentClientId, exp } 
        }));
      } else {
        // Token expired - just mark as initialized, don't dispatch logout
        // to avoid triggering logout in other tabs via storage event
        dispatch(markInitialized());
        localStorage.removeItem("authToken");
        localStorage.removeItem("merchantId");
        localStorage.removeItem("parentClientId");
      }
    } catch (err) {
      // Invalid token - just mark as initialized, don't dispatch logout
      console.warn("Invalid token during session restore:", err);
      dispatch(markInitialized());
      localStorage.removeItem("authToken");
      localStorage.removeItem("merchantId");
      localStorage.removeItem("parentClientId");
    }
  }, [dispatch]);

  return (
    <Router>
      <RootListener />  {/* in case you forgot it up top */}
      <ToastProvider>
        <Routes>
          <Route path="/" element={<Navigate replace to={isLoggedIn ? "/all-shipments" : getLoginRouteForCurrentSource()} />} />
          <Route path="/login" element={<LoginPage source="wayuvega" />} />
          <Route path="/login-jumbo" element={<JumboMailLogin />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/track" element={<PublicTrackingPage />} />
          <Route path="/merchant-signup" element={<MerchantOnboarding />} />

          <Route element={<RequireAuth />}>
            <Route element={<Layout />}>
              <Route path="/create-bulk-order" element={<CreateBulkOrder />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/create-forward-order" element={<CreateForwardOrder />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/marketplace-self-ship" element={<SelfShipOrders />} />
              <Route path="/generate-label" element={<GenerateLabel />} />
              <Route path="/all-shipments" element={<AllShipments />} />
              <Route path="/pickup-manifest" element={<PickupManifest />} />
              <Route path="/ndr-management" element={<NDRManagement />} />
              <Route path="/bulk-tracking" element={<BulkTracking />} />
              <Route path="/shipment-details" element={<ShipmentDetails />} />
              <Route path="/shipment-details/:orderRef" element={<ShipmentDetails />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/create-reverse-order" element={<CreateReverseOrder />} />
              <Route path="/client-onboarding" element={<ClientOnboarding />} />
              <Route path="/search-unprocessed-orders" element={<SearchUnprocessedOrdersPage />} />
              <Route path="/cod-remittance-reports" element={<CodRemittance />} />
              <Route path="/shipping-bulk-labels-download" element={<ShippingBulkLabelsDownload />} />
              <Route path="/channel-order" element={<ChannelOrdersPage />} />
              <Route path="/claim-tracker" element={<ClaimTracker />} />
              <Route path="/profile" element={<Profile />} />
            </Route>
          </Route>

          {/* Redirect unknown paths */}
          <Route path="*" element={<Navigate replace to={isLoggedIn ? "/create-bulk-order" : "/login"} />} />
        </Routes>
      </ToastProvider>
    </Router>
  );
};

export default App;
